// TanStack Query hooks for Predefined Factor Registry

import { useAuth } from '@clerk/clerk-react';
import { useQuery } from '@tanstack/react-query';

import type { PredefinedFactorListResponse, PredefinedFactorStatsResponse } from '../types';

import { useGHGOrganizationId } from './use-ghg-organization-id';

import { createApiClient } from '@/lib/api-client';

export const predefinedFactorKeys = {
  all: ['predefined-factors'] as const,
  list: (orgId: string, params: Record<string, unknown>) =>
    [...predefinedFactorKeys.all, 'list', orgId, params] as const,
  stats: (orgId: string) => [...predefinedFactorKeys.all, 'stats', orgId] as const,
};

export interface PredefinedFactorParams {
  scope?: number;
  category?: string;
  unitType?: string;
  query?: string;
  page?: number;
  pageSize?: number;
}

export function usePredefinedFactors(
  params: PredefinedFactorParams = {},
  options?: { enabled?: boolean }
) {
  const { getToken } = useAuth();
  const orgId = useGHGOrganizationId() ?? '';

  return useQuery({
    queryKey: predefinedFactorKeys.list(orgId, params),
    queryFn: async () => {
      const apiClient = createApiClient({ getAuthToken: getToken });
      const searchParams = new URLSearchParams();
      if (params.scope !== undefined) searchParams.set('scope', String(params.scope));
      if (params.category) searchParams.set('category', params.category);
      if (params.unitType) searchParams.set('unitType', params.unitType);
      if (params.query) searchParams.set('query', params.query);
      if (params.page) searchParams.set('page', String(params.page));
      if (params.pageSize) searchParams.set('pageSize', String(params.pageSize));
      const qs = searchParams.toString();
      return apiClient.get<PredefinedFactorListResponse>(
        `/organizations/${orgId}/ghg/predefined-factors${qs ? `?${qs}` : ''}`
      );
    },
    enabled: Boolean(orgId) && (options?.enabled ?? true),
  });
}

export function usePredefinedFactorStats(options?: { enabled?: boolean }) {
  const { getToken } = useAuth();
  const orgId = useGHGOrganizationId() ?? '';

  return useQuery({
    queryKey: predefinedFactorKeys.stats(orgId),
    queryFn: async () => {
      const apiClient = createApiClient({ getAuthToken: getToken });
      return apiClient.get<PredefinedFactorStatsResponse>(
        `/organizations/${orgId}/ghg/predefined-factors/stats`
      );
    },
    enabled: Boolean(orgId) && (options?.enabled ?? true),
  });
}
